import type { Item, Level, Vec2 } from '../core/hp/types';
import type { Footprints } from '../core/geometry/footprint';
import { landingElevation, type LandingPointer, type SurfaceProbe } from '../core/geometry/landing';
import { stackedItems, transformItemWithStack } from '../core/geometry/stacking';
import { t } from '../i18n/I18n';
import type { ItemIdentity } from '../collaboration/protocol';

/**
 * The height an item takes while it is dragged. Unity keeps the item's own height (a value typed
 * into the inspector, or the one the project was saved with) locked until the surface under the
 * item changes — `TopFurnitureHandler.IsPlainOffsetLocked` — and from then on the item follows
 * the surface: up onto a table, back down to the floor.
 */
interface LandingState { previous: number; unlocked: boolean; current: number; passengers: ReadonlySet<string> }
/** `copyUniqueId`: the copy's `UniqueId` when its identity came with one (a shared project's own). */
export interface ItemMoveGesture { base: Level; sourceId: string; copyId: string | null; copyUniqueId?: string; landing?: LandingState }
/** What the move knows about the pointer and the meshes, for landing the item on what it crosses. */
export interface MoveInput { pointer?: LandingPointer; probe?: SurfaceProbe; footprints?: Footprints; grab?: Vec2 }
export const exceedsDragThreshold = (dx: number, dy: number) => Math.hypot(dx, dy) > 4;

/** What a held plan drag hears of the store: what may be edited, and whether the store still holds the drag's stroke. */
export type HeldDragState = { edit: string; isDragging: boolean };
/**
 * Whether a held plan drag of `kind` ends with this store change: editing switched on or off (a link drop, a role change,
 * the session's end) or the store dropped the drag's stroke. A pull after that would commit the plan the drag began from,
 * outside any stroke. A pan and a ruler change nothing and go on.
 */
export function planDragLost(kind: string, before: HeldDragState, next: HeldDragState): boolean {
  if (kind === 'pan' || kind === 'measure') return false;
  return before.edit !== next.edit || (before.isDragging && !next.isDragging);
}

/** Calls `lost` whenever a held drag would end by `planDragLost`, for a view that holds its drag outside the plan; returns the unsubscribe. */
export function followHeldDrag(store: { get(): HeldDragState; subscribe(fn: () => void): () => void }, lost: () => void): () => void {
  let previous = store.get();
  return store.subscribe(() => {
    const next = store.get(), before = previous;
    previous = next;
    if (planDragLost('move', before, next)) lost();
  });
}

/** Where `source` lands once at `position`, through the gesture's lock on its own height. */
function landedElevation(gesture: ItemMoveGesture, source: Item, position: Vec2, input?: MoveInput): number {
  if (source.placement !== 'floor') return source.elevation;
  if (!gesture.landing) {
    const passengers = new Set(stackedItems(gesture.base.items, source, input?.probe).map(item => item.id));
    // Compare the first tick with its original support too: a shelf item can leave the
    // shelf immediately, while a typed height above an empty floor stays locked.
    const support = landingElevation(gesture.base, source, source.position, { probe: input?.probe,
      footprints: input?.footprints, exclude: passengers, currentElevation: source.elevation }).elevation;
    const previous = Math.abs(support - source.elevation) <= .04 ? support : 0;
    gesture.landing = { previous, unlocked: false, current: source.elevation, passengers };
  }
  const state = gesture.landing;
  const computed = landingElevation(gesture.base, source, position, { ...input, exclude: state.passengers, currentElevation: state.current }).elevation;
  if (!state.unlocked && Math.abs(computed - state.previous) > 1e-3) state.unlocked = true;
  state.previous = computed;
  state.current = state.unlocked ? computed : source.elevation;
  return state.current;
}

export function previewItemMove(gesture: ItemMoveGesture, position: Vec2, copy: boolean,
  idFactory: () => string | ItemIdentity = () => 'item-' + crypto.randomUUID(), input?: MoveInput): { level: Level; selectedId: string } {
  const source = gesture.base.items.find(item => item.id === gesture.sourceId);
  if (!source) throw new Error(t('#GeometryItemNotFound'));
  const elevation = landedElevation(gesture, source, position, input);
  if (copy) {
    if (gesture.copyId == null) {
      const identity = idFactory();
      gesture.copyId = typeof identity === 'string' ? identity : identity.id;
      gesture.copyUniqueId = typeof identity === 'string' ? undefined : identity.uniqueId;
    }
    if (gesture.base.items.some(item => item.id === gesture.copyId)) throw new Error(t('#PlanGestureCopyIdTaken'));
    const duplicate = structuredClone(source);
    duplicate.id = gesture.copyId;
    // Never the source's UniqueId: the app would read the two pieces as one.
    if (gesture.copyUniqueId) duplicate.uniqueId = gesture.copyUniqueId;
    else delete duplicate.uniqueId;
    duplicate.position = { ...position };
    duplicate.elevation = elevation;
    return { level: { ...gesture.base, items: [...gesture.base.items, duplicate] }, selectedId: duplicate.id };
  }
  if (source.position.x === position.x && source.position.z === position.z && elevation === source.elevation) return { level: gesture.base, selectedId: source.id };
  // Whatever stands on the item travels with it, up onto a surface too; a copy is a single new object, so it lands empty.
  return { level: transformItemWithStack(gesture.base, source.id, { position, rotation: source.rotation, elevation }, undefined, input?.probe), selectedId: source.id };
}

/**
 * A Shift-gathered group moved by `delta`: every item keeps its place in the arrangement, and
 * whatever stands on one of them travels too. Items hung on a wall stay on their wall: a shift
 * across the floor would tear them off it.
 */
export function moveItemGroup(base: Level, ids: readonly string[], delta: Vec2, probe?: SurfaceProbe): Level {
  if (!delta.x && !delta.z) return base;
  const moving = new Set<string>();
  for (const item of base.items) {
    if (!ids.includes(item.id) || item.placement === 'wall') continue;
    moving.add(item.id);
    for (const passenger of stackedItems(base.items, item, probe)) moving.add(passenger.id);
  }
  if (!moving.size) return base;
  return { ...base, items: base.items.map(item => moving.has(item.id)
    ? { ...item, position: { x: item.position.x + delta.x, z: item.position.z + delta.z } } : item) };
}

export function constrainMove(raw: Vec2, origin: Vec2, shift: boolean, locked?: 'x' | 'z'):
  { point: Vec2; axis?: 'x' | 'z' } {
  if (!shift) return { point: raw };
  const dx = Math.abs(raw.x - origin.x), dz = Math.abs(raw.z - origin.z);
  const axis = locked ?? (dx === dz ? undefined : dx > dz ? 'x' : 'z');
  return { point: axis === 'x' ? { x: raw.x, z: origin.z } : axis === 'z' ? { x: origin.x, z: raw.z } : raw, axis };
}

/**
 * The dead zone around the Wall Anchor (Figma «Зона вокруг Wall Anchor»): the pointer wanders
 * this far, in CSS pixels, without moving the anchor, so a wall's direction does not tremble
 * with the hand. A finger is coarser than a mouse and gets the wider zone.
 */
export const ANCHOR_ZONE = { fine: 10, coarse: 22 } as const;
/** How far above the finger the moving end of a wall sits during a touch drag, so the hand never hides it. */
export const TOUCH_ANCHOR_LIFT = 40;

/** The anchor stays put while `target` is within `radius` of it and follows, keeping that distance, once pulled beyond. */
export function pullAnchor(anchor: Vec2, target: Vec2, radius: number): Vec2 {
  const dx = target.x - anchor.x, dz = target.z - anchor.z, distance = Math.hypot(dx, dz);
  if (distance <= radius) return anchor;
  const k = (distance - radius) / distance;
  return { x: anchor.x + dx * k, z: anchor.z + dz * k };
}

/** Whether the direction from `from` to `to` lies on one of the standard drawing angles (multiples of 45°). */
export function standardAngle(from: Vec2, to: Vec2): boolean {
  const turns = Math.atan2(to.z - from.z, to.x - from.x) / (Math.PI / 4);
  return Math.abs(turns - Math.round(turns)) < 1e-6;
}

/** A plain rectangle of `thickness` around the centreline a→b, for ghosts that never join anything. */
export function wallQuad(a: Vec2, b: Vec2, thickness: number): Vec2[] {
  const dx = b.x - a.x, dz = b.z - a.z, span = Math.hypot(dx, dz) || 1;
  const nx = -dz / span * thickness / 2, nz = dx / span * thickness / 2;
  return [{ x: a.x + nx, z: a.z + nz }, { x: b.x + nx, z: b.z + nz }, { x: b.x - nx, z: b.z - nz }, { x: a.x - nx, z: a.z - nz }];
}
